import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import type { AssetDetail, UserListItem, Visibility } from "../api/types";
import { useAuth, canUpload } from "../auth/AuthContext";
import { Player } from "../components/Player";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Select } from "../components/Select";
import { TitleManager } from "../components/TitleManager";
import { formatDuration, monetizationLabel, relativeTime } from "../utils/format";
import { useT } from "../i18n";

const VIS_BADGE: Record<string, { label: string; color: string }> = {
  public: { label: "Public", color: "bg-green-700" },
  private: { label: "Private", color: "bg-gray-600" },
  protected: { label: "Protected", color: "bg-yellow-700" },
};

const DONE_STATES = new Set(["READY", "FAILED"]);

// Common language codes for the dub selector.
const LANGUAGES: { code: string; label: string }[] = [
  { code: "eng", label: "English" },
  { code: "zho", label: "Chinese" },
  { code: "jpn", label: "Japanese" },
  { code: "kor", label: "Korean" },
  { code: "fra", label: "French" },
  { code: "deu", label: "German" },
  { code: "spa", label: "Spanish" },
  { code: "ita", label: "Italian" },
  { code: "por", label: "Portuguese" },
  { code: "rus", label: "Russian" },
  { code: "tha", label: "Thai" },
  { code: "ara", label: "Arabic" },
  { code: "hin", label: "Hindi" },
];

function resolveTitle(asset: AssetDetail): string {
  return asset.resolvedTitle ?? asset.title;
}

export function WatchPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale } = useT();
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [manifestUrl, setManifestUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEditVis, setShowEditVis] = useState(false);
  const [editVis, setEditVis] = useState<Visibility>("private");
  const [editVisIds, setEditVisIds] = useState<string[]>([]);
  const [tenantUsers, setTenantUsers] = useState<UserListItem[]>([]);
  const [savingVis, setSavingVis] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio dub upload state.
  const [audioLang, setAudioLang] = useState("zho");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!id) return;
      try {
        const a = await api.getAsset(id, locale);
        if (cancelled) return;
        setAsset(a);
        if (a.status === "READY") {
          try {
            const pb = await api.getPlayback(id);
            if (!cancelled) setManifestUrl(pb.manifestUrl);
          } catch (e) {
            if (!cancelled) setError((e as Error).message);
          }
        } else if (!DONE_STATES.has(a.status)) {
          // Poll until the asset settles.
          pollRef.current = setTimeout(load, 3000);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }

    load();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [id, locale]);

  if (error) {
    return <div className="px-4 py-6 text-red-400">Error: {error}</div>;
  }

  if (!asset) {
    return <div className="px-4 py-6 text-brand-muted">Loading...</div>;
  }

  const processing = !DONE_STATES.has(asset.status);
  const displayTitle = resolveTitle(asset);

  async function handleDelete() {
    if (!asset || !id) return;
    setDeleting(true);
    try {
      await api.deleteAsset(id);
      navigate("/");
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  }

  async function saveTags(tags: string[]) {
    if (!id) return;
    setSavingTags(true);
    try {
      const updated = await api.updateAsset(id, { tags });
      setAsset(updated);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingTags(false);
    }
  }

  async function attachAudio() {
    if (!id || !audioFile) return;
    setAudioBusy(true);
    setAudioProgress(0);
    try {
      const label = LANGUAGES.find((l) => l.code === audioLang)?.label ?? audioLang;
      const slot = await api.createAudioAttach(id, {
        language: audioLang,
        label,
        fileSize: audioFile.size,
        multipart: audioFile.size > 8 * 1024 * 1024,
      });

      if (slot.multipart) {
        await api.uploadAudioMultipart(
          id,
          slot.sourceKey,
          audioFile,
          slot.multipart.partUrls,
          slot.multipart.partSize,
          slot.multipart.uploadId,
          (r) => setAudioProgress(r),
        );
      } else {
        await api.uploadFile(slot.uploadUrl, audioFile, (r) => setAudioProgress(r));
      }

      await api.ingestAudioAttach(id, {
        audioKey: slot.sourceKey,
        language: audioLang,
        label,
      });

      setAudioFile(null);
      setAudioProgress(0);
      // Re-trigger polling to refresh until the asset is READY again.
      const a = await api.getAsset(id, locale);
      setAsset(a);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAudioBusy(false);
    }
  }

  return (
    <div className="px-4 py-4 max-w-6xl mx-auto">
      {manifestUrl ? (
        <Player manifestUrl={manifestUrl} />
      ) : (
        <div className="w-full aspect-video rounded-xl bg-black flex items-center justify-center">
          {processing ? (
            <div className="text-center">
              <div className="inline-block w-10 h-10 border-4 border-brand-line border-t-brand-red rounded-full animate-spin mb-3" />
              <p className="text-brand-muted">
                {asset.status === "QUEUED"
                  ? t("watch.processing")
                  : t("watch.transcoding")}
              </p>
            </div>
          ) : (
            <div className="text-center text-brand-muted">
              <p className="text-lg">{t("watch.failed")}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        <h1 className="text-xl font-semibold">{displayTitle}</h1>
        <div className="flex items-center gap-3 mt-2 text-sm text-brand-muted">
          <StatusBadge status={asset.status} />
          {/* Visibility badge */}
          <span className={`px-1.5 py-0.5 rounded text-[11px] text-white ${VIS_BADGE[asset.visibility]?.color ?? "bg-gray-600"}`}>
            {t(`visibility.${asset.visibility}`)}
          </span>
          {asset.monetization !== "free" && (
            <span className="px-1.5 py-0.5 rounded text-[11px] bg-brand-line">
              {monetizationLabel(asset.monetization, t)}
            </span>
          )}
          <span>· {formatDuration(asset.durationSeconds)}</span>
          <span>· {relativeTime(asset.createdAt, t)}</span>
          {canUpload(user?.role) && (
            <>
              <button
                onClick={() => {
                  setEditVis(asset.visibility);
                  setEditVisIds((asset.visibleTo ?? []).map((v) => v.userId));
                  setShowEditVis(true);
                  api.listUsers(100).then((r) => setTenantUsers(r.data)).catch(() => {});
                }}
                className="ml-auto px-3 py-1 rounded text-xs bg-brand-surface hover:bg-brand-hover border border-brand-line text-brand-muted"
              >
                {t("watch.editVisibility")}
              </button>
              <button
                onClick={() => setShowDelete(true)}
                disabled={deleting}
                className="px-3 py-1 rounded text-xs bg-brand-surface hover:bg-red-900/40 border border-brand-line text-red-400 disabled:opacity-50"
              >
                {deleting ? t("common.deleting") : t("watch.delete")}
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 mt-3 pb-4 border-b border-brand-line">
          <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center text-sm font-semibold">
            {(asset.uploadedByEmail ?? "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium">{asset.uploadedByEmail ?? t("common.unknown")}</div>
            <div className="text-xs text-brand-muted">{t("watch.channel")}</div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pb-4 border-b border-brand-line">
          {asset.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-brand-hover border border-brand-line"
            >
              {tag}
              {canUpload(user?.role) && (
                <button
                  onClick={() => saveTags(asset.tags.filter((t) => t !== tag))}
                  disabled={savingTags}
                  className="text-brand-muted hover:text-brand-red"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          {canUpload(user?.role) && (
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagInput.trim()) {
                  e.preventDefault();
                  const t = tagInput.trim();
                  if (!asset.tags.includes(t)) {
                    saveTags([...asset.tags, t]);
                  }
                  setTagInput("");
                }
              }}
              placeholder={t("upload.addTag")}
              disabled={savingTags}
              className="px-2 py-1 rounded-full text-sm bg-brand-surface border border-brand-line focus:border-brand-red outline-none w-28"
            />
          )}
          {asset.tags.length === 0 && !canUpload(user?.role) && (
            <span className="text-xs text-brand-muted">{t("watch.noTags")}</span>
          )}
        </div>
      </div>

      {/* Title management */}
      {canUpload(user?.role) && (
        <div className="mt-4 pb-4 border-b border-brand-line">
          <TitleManager assetId={asset.id} titles={asset.titles ?? []} />
        </div>
      )}

      {processing && (
        <div className="mt-4 p-4 rounded-xl bg-brand-surface text-sm text-brand-muted">
          {t("watch.processingNote")}
        </div>
      )}

      {asset.renditions.length > 0 && (
        <div className="mt-4">
          <h2 className="text-sm font-semibold mb-2">{t("watch.renditions")}</h2>
          <div className="flex flex-wrap gap-2">
            {asset.renditions.map((r) => (
              <span
                key={r.profile}
                className="px-2 py-1 rounded bg-brand-surface text-xs"
              >
                {r.profile} · {Math.round(r.bitrate / 1000)}kbps · {r.codec}
              </span>
            ))}
          </div>
        </div>
      )}

      {(asset.audioTracks?.length > 0 || (canUpload(user?.role) && asset.status === "READY")) && (
        <div className="mt-4">
          <h2 className="text-sm font-semibold mb-2">{t("watch.audioTracks")}</h2>
          <div className="flex flex-wrap gap-2">
            {asset.audioTracks?.map((track) => (
              <span
                key={track.language}
                className="px-2 py-1 rounded bg-brand-surface text-xs"
              >
                {track.label}
                {track.isDefault && <span className="text-brand-muted"> ({t("common.default")})</span>}
                <span className="text-brand-muted"> · {track.channels}ch · {track.codec}</span>
              </span>
            ))}
            {asset.audioTracks?.length === 0 && !canUpload(user?.role) && (
              <span className="text-xs text-brand-muted">{t("watch.noAudioTracks")}</span>
            )}
          </div>

          {canUpload(user?.role) && asset.status === "READY" && (
            <div className="mt-3 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-brand-surface border border-brand-line">
              <Select
                value={audioLang}
                options={LANGUAGES.map((l) => ({ value: l.code, label: `${l.label} (${l.code})` }))}
                onChange={setAudioLang}
                disabled={audioBusy}
                size="sm"
              />
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)}
                disabled={audioBusy}
                className="text-xs file:mr-2 file:px-2 file:py-1 file:rounded file:border file:border-brand-line file:bg-brand-dark file:text-brand-text"
              />
              <button
                onClick={attachAudio}
                disabled={audioBusy || !audioFile}
                className="px-3 py-1 rounded text-xs bg-brand-red text-white disabled:opacity-50"
              >
                {audioBusy ? t("watch.attaching") : t("watch.attachAudio")}
              </button>
              {audioBusy && (
                <div className="flex-1 min-w-[120px]">
                  <div className="h-1.5 rounded-full bg-brand-line overflow-hidden">
                    <div className="h-full bg-brand-red" style={{ width: `${Math.round(audioProgress * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {asset.jobs.length > 0 && (
        <div className="mt-4">
          <h2 className="text-sm font-semibold mb-2">{t("watch.processingJobs")}</h2>
          <div className="space-y-1">
            {asset.jobs.map((j, i) => (
              <div key={i} className="text-xs text-brand-muted flex gap-2">
                <span className="w-16">{j.profile}</span>
                <span className="w-24">{j.status}</span>
                {j.errorMessage && <span className="text-red-400">{j.errorMessage}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showDelete}
        title={t("watch.deleteTitle")}
        message={t("watch.deleteMessage", { title: displayTitle })}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />

      {/* Edit visibility modal */}
      {showEditVis && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowEditVis(false)}>
          <div className="w-full max-w-md mx-4 rounded-xl bg-brand-surface border border-brand-line shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <h2 className="text-base font-semibold mb-3">{t("watch.editVisibilityTitle")}</h2>
              <div className="flex flex-wrap gap-3 mb-4">
                {(["private", "public", "protected"] as const).map((v) => (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer ${
                      editVis === v
                        ? "border-brand-red bg-brand-red/10 text-brand-text"
                        : "border-brand-line bg-brand-dark text-brand-muted"
                    }`}
                  >
                    <input type="radio" name="editVis" checked={editVis === v} onChange={() => setEditVis(v)} className="hidden" />
                    <span className="text-sm capitalize">{t(`visibility.${v}`)}</span>
                  </label>
                ))}
              </div>
              {editVis === "protected" && (
                <div className="mb-4">
                  <label className="block text-xs text-brand-muted mb-1">{t("watch.grantAccess")}</label>
                  {tenantUsers.length === 0 ? (
                    <p className="text-xs text-brand-muted">{t("upload.loadingUsers")}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {tenantUsers.filter((u) => u.id !== user?.id).map((u) => (
                        <label
                          key={u.id}
                          className={`px-2.5 py-1 rounded-full text-xs border cursor-pointer ${
                            editVisIds.includes(u.id)
                              ? "bg-brand-red border-brand-red text-white"
                              : "bg-brand-dark border-brand-line text-brand-muted"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={editVisIds.includes(u.id)}
                            onChange={() => setEditVisIds((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])}
                          />
                          {u.email}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setShowEditVis(false)} className="px-4 py-2 rounded-full text-sm bg-brand-hover">{t("common.cancel")}</button>
              <button
                onClick={async () => {
                  if (!id) return;
                  setSavingVis(true);
                  try {
                    const updated = await api.updateAsset(id, {
                      visibility: editVis,
                      visibleToIds: editVis === "protected" ? editVisIds : undefined,
                    });
                    setAsset(updated);
                    setShowEditVis(false);
                  } catch (err) {
                    setError((err as Error).message);
                  } finally {
                    setSavingVis(false);
                  }
                }}
                disabled={savingVis || (editVis === "protected" && editVisIds.length === 0)}
                className="px-4 py-2 rounded-full text-sm bg-brand-red text-white disabled:opacity-50"
              >
                {savingVis ? t("common.saving") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
